import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Force every client-side route change to land at the top of the new page.
 *
 * Handles three things that can otherwise leave the viewport mid-page:
 *   1. The browser's built-in `history.scrollRestoration = "auto"` re-applies
 *      the previous scroll position after a route change. We flip it to
 *      "manual" once, on first mount.
 *   2. React Router mounts the new page synchronously, but layout / images
 *      finish painting a few frames later. If we only scroll once (before
 *      paint) a tall new page can visually stay mid-scroll on some browsers.
 *      We scroll immediately AND on the next animation frame.
 *   3. If the URL has a hash (`/page#section`) we scroll to that element
 *      instead of the top.
 */
export default function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        el.scrollIntoView({ behavior: "auto", block: "start" });
        return;
      }
    }

    const jump = () => {
      // The site sets `html { scroll-behavior: smooth }`, which would
      // ANIMATE this scrollTo(0,0) over ~600 ms and make the new page
      // visibly mount mid-scroll. We locally force `auto` (instant) for the
      // reset, then restore whatever value was there so in-page anchor
      // links keep their smooth-scroll behaviour elsewhere.
      const html = document.documentElement;
      const prev = html.style.scrollBehavior;
      html.style.scrollBehavior = "auto";
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      if (document.documentElement) document.documentElement.scrollTop = 0;
      if (document.body) document.body.scrollTop = 0;
      html.style.scrollBehavior = prev;
    };

    jump();
    // Run again after the browser finishes painting the new route, so any
    // late-mounting layout (hero images, data fetches) can't leave the
    // viewport mid-page.
    const raf = window.requestAnimationFrame(jump);
    const t = window.setTimeout(jump, 60);

    return () => {
      window.cancelAnimationFrame(raf);
      window.clearTimeout(t);
    };
  }, [pathname, hash]);

  return null;
}
